import * as ClientConfig from "../../config/ClientConfig.js";
import * as StorageService from "../StorageServices.js";
import { LOCALSTORAGE_ITEMS } from "../../util/Constants";
import { handleAuthEmptyResponse, handleAuthFileResponse, handleAuthResponse } from "../../util/api/restClient";

/**
 * Helper: build query string, supporting arrays (repeat key).
 * Example: { statuses: ["PENDING","SIGNED"], sort: ["created,DESC"] }
 * => ?statuses=PENDING&statuses=SIGNED&sort=created,DESC
 */
const toQueryString = (params = {}) => {
  const sp = new URLSearchParams();

  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;

    if (Array.isArray(v)) {
      v.filter((x) => x !== undefined && x !== null && x !== "").forEach((x) => sp.append(k, String(x)));
      return;
    }

    sp.append(k, String(v));
  });

  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  "Authorization": "Bearer " + StorageService.getItem(LOCALSTORAGE_ITEMS.ACCESS_TOKEN),
});

// ============================
// GET /api/client-form
// ============================
const getAllClientForms = async (queryParams) => {
  return handleAuthResponse(() => {
    const finalURL =
      ClientConfig.URL_RESOURCE_SERVER +
      "/api/client-form" +
      toQueryString(queryParams);

    return fetch(finalURL, {
      method: "GET",
      headers: authHeaders(),
    });
  });
};

// ============================
// POST /api/client-form
// ============================
const createClientForm = async (body) => {
  return handleAuthResponse(() => {
    const finalURL = ClientConfig.URL_RESOURCE_SERVER + "/api/client-form";
    return fetch(finalURL, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
  });
};

// ============================
// GET /api/client-form/{id}
// ============================
const getClientFormById = async (id) => {
  return handleAuthResponse(() => {
    const finalURL = ClientConfig.URL_RESOURCE_SERVER + "/api/client-form/" + id;
    return fetch(finalURL, {
      method: "GET",
      headers: authHeaders(),
    });
  });
};

// ============================
// PUT /api/client-form/{id}
// ============================
const editClientFormById = async (id, body) => {
  return handleAuthResponse(() => {
    const finalURL = ClientConfig.URL_RESOURCE_SERVER + "/api/client-form/" + id;
    return fetch(finalURL, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
  });
};

// ============================
// DELETE /api/client-form/{id}
// (Swagger says 200 OK, often empty body -> use handleAuthEmptyResponse)
// ============================
const deleteClientFormById = async (id) => {
  return handleAuthEmptyResponse(() => {
    const finalURL = ClientConfig.URL_RESOURCE_SERVER + "/api/client-form/" + id;
    return fetch(finalURL, {
      method: "DELETE",
      headers: authHeaders(),
    });
  });
};

// ============================
// GET /api/client-form/me
// ============================
const getMyClientForms = async (queryParams) => {
  
  return handleAuthResponse(() => {
    const finalURL =
      ClientConfig.URL_RESOURCE_SERVER +
      "/api/client-form/me" +
      toQueryString(queryParams);

    return fetch(finalURL, {
      method: "GET",
      headers: authHeaders(),
    });
  });
};

// ============================
// GET /api/client-form/sign/{id}
// ============================
const getClientFormSignById = async (id) => {
  return handleAuthResponse(() => {
    const finalURL = ClientConfig.URL_RESOURCE_SERVER + "/api/client-form/sign/" + id;
    return fetch(finalURL, {
      method: "GET",
      headers: authHeaders(),
    });
  });
};

// ============================
// PUT /api/client-form/sign/{id}
// ============================
const signClientFormById = async (id, body) => {
  return handleAuthResponse(() => {
    const finalURL = ClientConfig.URL_RESOURCE_SERVER + "/api/client-form/sign/" + id;
    return fetch(finalURL, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
  });
};

// ============================
// GET /api/client-form/export/{fileName}.xlsx
// ============================
const exportClientFormsExcel = async (fileName, queryParams) => {
  return handleAuthFileResponse(() => {
    const finalURL =
      ClientConfig.URL_RESOURCE_SERVER +
      "/api/client-form/export/" +
      encodeURIComponent(fileName) +
      ".xlsx" +
      toQueryString(queryParams);

    return fetch(finalURL, {
      method: "GET",
      headers: authHeaders(),
    });
  });
};

export {
  getAllClientForms,
  exportClientFormsExcel,
  createClientForm,
  getClientFormById,
  editClientFormById,
  deleteClientFormById,
  getMyClientForms,
  getClientFormSignById,
  signClientFormById,
};
